import { useState, type FormEvent } from 'react';
import { Link } from '../../../components/Link';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';
import { Icon } from '../../../components/icons';
import { Button, Card, Field, Input, cn } from '../../../components/ui';
import { DeferredTurnstileWidget } from '../../../components/TurnstileWidget';
import { useTurnstileGate } from '../../../lib/useAbuseConfig';
import { ToolPageLayout, type ToolPageContent } from './ToolPageLayout';

interface SmtpStep {
  kind: 'connect' | 'banner' | 'send' | 'receive' | 'tls' | 'error' | 'close';
  text: string;
  elapsedMs: number;
}

interface SmtpResult {
  ok: boolean;
  error?: string;
  metrics: { connectMs: number | null; bannerMs: number | null; starttlsMs: number | null; totalMs: number };
  banner: string | null;
  capabilities: string[];
  tlsUpgraded: boolean | null;
  transcript: SmtpStep[];
}

const STEP_STYLE: Record<SmtpStep['kind'], string> = {
  connect: 'text-faint',
  banner: 'text-allow',
  send: 'text-accent',
  receive: 'text-txt',
  tls: 'text-verify',
  error: 'text-review',
  close: 'text-faint',
};

const STEP_PREFIX: Record<SmtpStep['kind'], string> = {
  connect: '·',
  banner: '◀',
  send: '▶',
  receive: '◀',
  tls: '⚿',
  error: '✗',
  close: '·',
};

/**
 * Free SMTP test tool — hits /public/tools/smtp-test. The backend opens a raw
 * socket to the supplied host, runs EHLO / STARTTLS / MAIL FROM / RCPT TO with
 * the full transcript captured. Never sends DATA, so this can't be abused to
 * send actual mail.
 */
export function SmtpTestPage() {
  const [host, setHost] = useState('');
  const [port, setPort] = useState(587);
  const [fromAddress, setFromAddress] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [useTls, setUseTls] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gate = useTurnstileGate();

  const test = useMutation<SmtpResult, ApiError>({
    mutationFn: () =>
      api<SmtpResult>('/public/tools/smtp-test', {
        method: 'POST',
        body: { host, port, fromAddress, toAddress: toAddress || undefined, useTls },
        auth: false,
        headers: gate.headers,
      }),
    onSuccess: () => {
      setRateLimited(false);
      setError(null);
    },
    onError: (e) => {
      if (e.status === 429) {
        setRateLimited(true);
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : 'SMTP test failed.');
        setRateLimited(false);
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!host.trim() || !fromAddress.trim()) return;
    test.mutate();
  }

  const content: ToolPageContent = {
    slug: 'smtp-test',
    title: 'SMTP test tool',
    tagline: 'Probe any SMTP server live — see the full wire transcript.',
    eyebrow: 'Free tool · No signup · Diagnostic only',
    description:
      'Free SMTP test tool. Connect to any SMTP server, run EHLO / STARTTLS / MAIL FROM and watch the full transcript. Diagnose connection, TLS and auth issues live.',
    icon: <Icon.Server />,
    accent: 'accent',
    intro:
      "Enter a host + port + sender address. We open a raw socket, run the SMTP handshake one verb at a time, and stream the transcript back. Never sends DATA — purely a diagnostic, so this can't be used to actually deliver mail.",
    body: (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.4fr]">
        <Card className="p-6">
          <h2 className="text-sm font-semibold">SMTP target</h2>
          <form onSubmit={onSubmit} {...gate.armProps} className="mt-5 grid gap-4">
            <Field label="Host">
              <Input
                value={host}
                onChange={(e) => setHost(e.target.value.trim())}
                placeholder="smtp.gmail.com"
                maxLength={253}
                autoFocus
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Port" hint="25 / 465 (SMTPS) / 587 (submission)">
                <Input
                  type="number"
                  value={port}
                  onChange={(e) => setPort(Math.max(1, Math.min(65535, parseInt(e.target.value, 10) || 0)))}
                  min={1}
                  max={65535}
                />
              </Field>
              <Field label="Implicit TLS?" hint="Use for port 465 (SMTPS).">
                <label className="flex h-11 items-center gap-2 rounded-md border border-border bg-bg px-3 text-[13px]">
                  <input
                    type="checkbox"
                    checked={useTls}
                    onChange={(e) => setUseTls(e.target.checked)}
                    className="h-4 w-4 accent-[var(--color-accent)]"
                  />
                  Open over TLS immediately
                </label>
              </Field>
            </div>
            <Field label="MAIL FROM address">
              <Input
                type="email"
                value={fromAddress}
                onChange={(e) => setFromAddress(e.target.value.trim())}
                placeholder="probe@yourdomain.com"
                maxLength={320}
              />
            </Field>
            <Field label="Optional RCPT TO address" hint="Provide a recipient to test deliverability. We RSET before any DATA, so no message is actually sent.">
              <Input
                type="email"
                value={toAddress}
                onChange={(e) => setToAddress(e.target.value.trim())}
                placeholder="(leave blank to skip)"
                maxLength={320}
              />
            </Field>

            <DeferredTurnstileWidget gate={gate} />
            <Button
              type="submit"
              variant="primary"
              disabled={test.isPending || !host.trim() || !fromAddress.trim() || !gate.ready}
            >
              {test.isPending ? 'Probing server…' : 'Run SMTP test'}
            </Button>
            {error && <p className="text-[13px] text-review">{error}</p>}
            {rateLimited && (
              <p className="text-[13px] text-verify">
                You've hit the free daily limit (4 / IP). <Link to="/register" className="text-accent hover:underline">Create a workspace</Link> for unlimited tests.
              </p>
            )}

            <p className="rounded-md border border-bordersoft bg-panel2 px-3 py-2 text-[12px] text-faint">
              Never sends DATA. The handshake stops at RCPT TO and exits with RSET + QUIT, so this is a connectivity / TLS / addressing diagnostic only — never an outbound mail relay.
            </p>
          </form>
        </Card>

        <Card className="p-6">
          <h2 className="text-sm font-semibold">Transcript</h2>
          {!test.data && (
            <div className="mt-4 flex h-64 items-center justify-center text-center text-[13px] text-muted">
              Run the probe to see the SMTP conversation here.
            </div>
          )}
          {test.data && (
            <>
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Metric label="Connect" value={test.data.metrics.connectMs} unit="ms" />
                <Metric label="Banner" value={test.data.metrics.bannerMs} unit="ms" />
                <Metric label="STARTTLS" value={test.data.metrics.starttlsMs} unit="ms" />
                <Metric label="Total" value={test.data.metrics.totalMs} unit="ms" />
              </div>

              <div
                className={cn(
                  'mt-4 rounded-md border px-3 py-2 text-[12.5px]',
                  test.data.ok
                    ? 'border-allow/30 bg-allow/10 text-allow'
                    : 'border-review/30 bg-review/10 text-review',
                )}
              >
                {test.data.ok ? 'Handshake completed successfully.' : `Failed: ${test.data.error}`}
                {test.data.tlsUpgraded === true && ' · TLS active'}
                {test.data.tlsUpgraded === false && ' · TLS not used'}
              </div>

              <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap break-all rounded-md border border-bordersoft bg-panel2 p-3 font-mono text-[11.5px] leading-relaxed">
                {test.data.transcript.map((s, i) => (
                  <div key={i} className={STEP_STYLE[s.kind]}>
                    <span className="opacity-50">[{String(s.elapsedMs).padStart(5, ' ')}ms] {STEP_PREFIX[s.kind]} </span>
                    {s.text}
                  </div>
                ))}
              </pre>

              {test.data.capabilities.length > 0 && (
                <details className="mt-3 rounded-md border border-bordersoft bg-panel p-3">
                  <summary className="cursor-pointer text-[12.5px] font-semibold">
                    EHLO capabilities ({test.data.capabilities.length})
                  </summary>
                  <ul className="mt-2 grid gap-1 font-mono text-[11.5px] text-muted">
                    {test.data.capabilities.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </Card>
      </div>
    ),
    educational: {
      title: 'What this probe does, and deliberately does not',
      paragraphs: [
        'It runs EHLO, STARTTLS, MAIL FROM and RCPT TO and shows you the full transcript, including handshake timing. Which command draws the error is what narrows the diagnosis: connection means an IP-level block, MAIL FROM means the sender domain, RCPT TO means the address itself.',
        'It never AUTHs and never sends DATA. AUTH would mean handling credentials on a public tool; DATA would make this a way to send mail, which is an obvious abuse surface. The four commands it does run cover the overwhelming majority of real diagnostic cases.',
      ],
    },
    faq: [
      { q: 'Can I test auth?', a: 'Not from the free tool — we don\'t want to handle credentials. The Inbox Placement add-on inside SieveGuard runs full authenticated SMTP tests on every mailbox you connect.' },
      { q: 'Why does it stop before DATA?', a: 'So this can\'t be used to send actual mail. The diagnostic value is in EHLO / STARTTLS / MAIL FROM / RCPT TO — those four cover almost every "why isn\'t my mail sending" question.' },
      { q: 'Which port should I use?', a: '587 for modern submission (most ESPs), 465 for implicit TLS (older but still common), 25 for server-to-server. 25 from your laptop is usually blocked by your ISP.' },
      { q: 'My ISP blocks port 25 — what gives?', a: 'Residential ISPs block outbound 25 to slow botnet-spam. From your laptop, test against port 587 instead. From a server, port 25 should work if your hosting provider hasn\'t also blocked it (some do).' },
      { q: 'TLS not used — bad?', a: 'On port 587 / 465, yes — modern mail servers expect TLS. Port 25 server-to-server is allowed to fall back to plaintext if STARTTLS isn\'t advertised, though most major receivers now expect it.' },
    ],
    relatedTools: [
      { slug: 'domain-checker', title: 'Domain health checker', tagline: 'Composite SPF / DKIM / DMARC / MX score for any domain.' },
      { slug: 'blacklist-checker', title: 'Email blacklist checker', tagline: 'Query 12+ public RBLs for any IP or domain.' },
      { slug: 'dkim-generator', title: 'DKIM key generator', tagline: 'Generate the keypair your mail server uses to sign outbound.' },
    ],
    ctaTitle: 'Authenticated SMTP testing on every mailbox',
    ctaBody:
      "SieveGuard's Mailbox Warmup add-on runs authenticated EHLO / AUTH / send tests on every mailbox you connect, daily, with full deliverability scoring per ESP. Free during your trial.",
  };

  return <ToolPageLayout content={content} />;
}

function Metric({ label, value, unit }: { label: string; value: number | null; unit: string }) {
  return (
    <div className="rounded-md border border-bordersoft bg-bg px-3 py-2">
      <div className="font-mono text-[10px] uppercase tracking-wider text-faint">{label}</div>
      <div className="mt-0.5 font-mono text-[14px] tabular-nums">
        {value == null ? '—' : `${value}`}
        {value != null && <span className="ml-0.5 text-[10px] text-faint">{unit}</span>}
      </div>
    </div>
  );
}

// Default export required by React Router framework-mode route modules.
export default SmtpTestPage;
